import { Navigation } from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { z } from "zod";

const AddWeight = () => {
    const { toast } = useToast();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { authFetch } = useAuth();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const WeightLogSchema = z.object({
        weight: z.coerce.number(),
        notes: z.string().optional(),
        date: z.string(),
    });

    type WeightFormData = z.infer<typeof WeightLogSchema>;

    const form = useForm<WeightFormData>({
        resolver: zodResolver(WeightLogSchema),
        defaultValues: {
            weight: 0,
            date: new Date().toISOString().slice(0, 10),
            notes: "",
        },
    });

    const onSubmit = async (data: WeightFormData) => {
        try {
            setIsSubmitting(true);
            const response = await authFetch("/api/weight/log", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(data),
            });

            if (!response.ok) {
                throw new Error(t("weight.form.error"));
            }

            const result = await response.json();

            toast({
                title: t("weight.toast.save"),
                description: t("weight.toast.success"),
            });

            navigate("/dashboard");
        } catch (error) {
            toast({
                variant: "destructive",
                title: t("weight.toast.save"),
                description: error instanceof Error ? error.message : t("weight.toast.error"),
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <Navigation />

            <div className="pt-24 pb-6 px-4 sm:px-6 lg:px-8">
                <div className="max-w-2xl mx-auto">
                    <h1 className="text-3xl font-bold text-gray-900 mb-8">{t("weight.form.title")}</h1>

                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                            <FormField
                                control={form.control}
                                name="date"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t("weight.form.date")}</FormLabel>
                                        <FormControl>
                                            <Input type="date" {...field} />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="weight"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t("weight.form.weight")}</FormLabel>
                                        <FormControl>
                                            <Input {...field} type="number" placeholder={t("weight.form.weightPlaceholder")} />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="notes"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t("weight.form.notes")}</FormLabel>
                                        <FormControl>
                                            <Textarea {...field} placeholder={t("weight.form.notesPlaceholder")} className="min-h-[200px]" />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />

                            <Button type="submit" disabled={isSubmitting} className="w-full">
                                {isSubmitting ? t("weight.form.saving") : t("weight.form.save")}
                            </Button>
                        </form>
                    </Form>
                </div>
            </div>
        </div>
    );
};

export default AddWeight;
